import React, { useState } from "react";
import { Layout, Menu, Flex, Button } from "antd";
import type { MenuProps } from "antd";
import {
  EditOutlined,
  SettingOutlined,
  NotificationOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  QrcodeOutlined,
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ApiOutlined,
  DesktopOutlined,
  AppstoreOutlined,
  ShopOutlined,
  CarOutlined,
  ShoppingCartOutlined,
  GiftOutlined,
  UserOutlined,
  InboxOutlined,
  FieldTimeOutlined,
  CreditCardOutlined,
  CalendarOutlined,
  ShoppingOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "./Page";
import { useAntdTheme, useFeatureFlags } from "../../hooks";
import { useAuth } from "../../contexts";

const { Sider, Content } = Layout;

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [collapsed, setCollapsed] = useState(false);
  const { authState } = useAuth();
  const { isEnabled } = useFeatureFlags(authState.currentRestaurant?.id);
  const prepaidCardEnabled = isEnabled("PrepaidCard");

  // Determine page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/admin") return t("admin_dashboard");
    if (path === "/admin/editor") return t("edit_menu");
    if (path === "/admin/inventory") return t("limited_quantity_inventory");
    if (path === "/admin/item-modification-schema") return t("edit_customization_options");
    if (path === "/admin/combo-addons") return t("combo_addons");
    if (path === "/admin/campaigns") return t("campaigns");
    if (path === "/admin/campaign-editor") return t("custom_order_page");
    if (path === "/admin/members") return t("manage_members");
    if (path === "/admin/shipping") return t("shipping_orders");
    if (path === "/admin/stats") return t("statistics");
    if (path === "/admin/reports") return t("reports");
    if (path === "/admin/qr-code") return t("in_store_qr_code");
    if (path === "/admin/announcements") return t("announcement_settings");
    if (path === "/admin/flags") return t("feature_flags");
    if (path === "/admin/feature-flags") return t("feature_flags");
    if (path === "/admin/square-integrations") return t("square_integrations");
    if (path === "/admin/point-gift-cards") return t("point_gift_cards");
    if (path === "/admin/workstation-schemas") return t("workstation_schemas");
    if (path === "/admin/item-schema-groups") return t("item_schema_groups");
    if (path === "/admin/restaurant-public-info") return t("restaurant_public_info");
    if (path === "/admin/points-config") return t("points_config");
    if (path === "/admin/wait-config") return t("wait_config");
    if (path === "/admin/large-volume-delivery") return t("large_volume_delivery");
    if (path === "/admin/large-volume-pickup-config") return t("large_volume_pickup_config");
    if (path === "/admin/promo-codes") return t("promo_codes");
    if (path === "/admin/prepaid-cards") return t("prepaid_cards");
    if (path === "/admin/prepaid-card-stats") return t("prepaid_card_stats");
    if (path === "/admin/users") return t("user_management");
    return t("admin_dashboard");
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "/admin",
      icon: <DashboardOutlined />,
      label: t("admin_dashboard"),
    },
    {
      type: "divider",
    },
    {
      key: "menu-group",
      label: t("menu_management"),
      type: "group",
      children: [],
    },
    {
      key: "/admin/workstation-schemas",
      icon: <DesktopOutlined />,
      label: t("workstation_schemas"),
    },
    {
      key: "/admin/item-schema-groups",
      icon: <AppstoreOutlined />,
      label: t("item_schema_groups"),
    },
    {
      key: "/admin/item-modification-schema",
      icon: <SettingOutlined />,
      label: t("edit_customization_options"),
    },
    {
      key: "/admin/combo-addons",
      icon: <ShoppingOutlined />,
      label: t("combo_addons"),
    },
    {
      key: "/admin/editor",
      icon: <EditOutlined />,
      label: t("edit_menu"),
    },
    {
      key: "/admin/inventory",
      icon: <InboxOutlined />,
      label: t("limited_quantity_inventory"),
    },
    {
      key: "/admin/promo-codes",
      icon: <GiftOutlined />,
      label: t("promo_codes"),
    },
    {
      key: "/admin/campaigns",
      icon: <CalendarOutlined />,
      label: t("campaigns"),
    },
    ...(prepaidCardEnabled
      ? [
          {
            key: "/admin/prepaid-cards",
            icon: <CreditCardOutlined />,
            label: t("prepaid_cards"),
          },
          {
            key: "/admin/prepaid-card-stats",
            icon: <BarChartOutlined />,
            label: t("prepaid_card_stats"),
          },
        ]
      : []),
    {
      type: "divider",
    },
    {
      key: "members-group",
      label: t("members_management"),
      type: "group",
      children: [],
    },
    {
      key: "/admin/members",
      icon: <TeamOutlined />,
      label: t("manage_members"),
    },
    {
      key: "/admin/point-gift-cards",
      icon: <GiftOutlined />,
      label: t("point_gift_cards"),
    },
    {
      type: "divider",
    },
    {
      key: "data-group",
      label: t("data_analytics"),
      type: "group",
      children: [],
    },
    {
      key: "/admin/stats",
      icon: <BarChartOutlined />,
      label: t("statistics"),
    },
    {
      key: "/admin/reports",
      icon: <FileTextOutlined />,
      label: t("reports"),
    },
    {
      type: "divider",
    },
    {
      key: "settings-group",
      label: t("settings"),
      type: "group",
      children: [],
    },
    {
      key: "/admin/users",
      icon: <UserOutlined />,
      label: t("user_management"),
    },
    {
      key: "/admin/qr-code",
      icon: <QrcodeOutlined />,
      label: t("in_store_qr_code"),
    },
    {
      key: "/admin/announcements",
      icon: <NotificationOutlined />,
      label: t("announcement_settings"),
    },
    {
      key: "/admin/square-integrations",
      icon: <ApiOutlined />,
      label: t("square_integrations"),
    },
    {
      key: "/admin/restaurant-public-info",
      icon: <ShopOutlined />,
      label: t("restaurant_public_info"),
    },
    {
      key: "/admin/points-config",
      icon: <GiftOutlined />,
      label: t("points_config"),
    },
    {
      key: "/admin/wait-config",
      icon: <FieldTimeOutlined />,
      label: t("wait_config"),
    },
    {
      type: "divider",
    },
    {
      key: "large-volume-group",
      label: t("large_volume_management"),
      type: "group",
      children: [],
    },
    {
      key: "/admin/large-volume-delivery",
      icon: <CarOutlined />,
      label: t("large_volume_delivery"),
    },
    {
      key: "/admin/large-volume-pickup-config",
      icon: <ShoppingCartOutlined />,
      label: t("large_volume_pickup_config"),
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "/admin") {
      navigate("/admin");
    } else if (key.startsWith("/admin/")) {
      navigate(key);
    }
  };

  return (
    <Page title={getPageTitle()} noCard={true}>
      <Layout style={{ minHeight: "100%", background: "transparent" }}>
        {/* Sidebar */}
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={280}
          collapsedWidth={80}
          trigger={null}
          style={{
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          {/* Collapse Toggle */}
          <Flex
            justify={collapsed ? "center" : "flex-end"}
            style={{
              padding: token.paddingMD,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: token.fontSizeLG,
                color: token.colorPrimary,
              }}
            />
          </Flex>

          {/* Menu */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            onClick={handleMenuClick}
            items={menuItems}
            style={{
              border: "none",
            }}
          />
        </Sider>

        {/* Main Content */}
        <Content style={{ padding: token.paddingMD }}>
          <div
            style={{
              background: token.colorBgContainer,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowTertiary,
              padding: token.paddingLG,
            }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Page>
  );
};

export default AdminLayout;
